import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from '@shared/services/toastr.service';
import { AuthService, CLUSTER_DELEGATION_ADMIN, CLUSTER_DELEGATION_USE } from '@shared/services/auth.service';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  mergeMap,
  NEVER,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { isValidEmail } from '@shared/validators/validators';
import { AdminOrganizationInfo, AdminOrganizationService } from '@shared/services/admin-organization.service';
import { Country, CountryService } from '@shared/services/country.service';
import { AsyncPipe, Location, NgFor, NgIf } from '@angular/common';
import { EditMode } from '@shared/edit.mode';
import { AdminClusterService, Cluster } from '@shared/services/admin-cluster.service';
import { ClusterRole } from '@shared/services/organization-cluster.service';
import { ClusterDelegationsService } from '@shared/services/cluster-delegations.service';
import { Contract, EditableContract } from '@shared/models/contract';
import { ClusterUidAndName } from '@shared/models/cluster';
import { AdminContractService, ContractTypes } from '@shared/services/admin-contract.service';
import { PageParams } from '@shared/services/logs/logs.service';
import { FEATURE_FLAGS } from 'config/feature-flags';
import { HelpComponent } from '@shared/components/help/help.component';
import { BadgeOrganizationComponent } from '@shared/components/badge-organization/badge-organization.component';
import { FormsModule } from '@angular/forms';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { AdminOrganizationUsersComponent } from './organization-users/admin-organization-users.component';
import { OrganizationContractsComponent } from './organization-contracts/organization-contracts.component';
import { TranslatePipe } from '@ngx-translate/core';
import { LabelComponent } from '@shared/components/label/label.component';
import { FeatureFlagDirective } from '@shared/utils/feature-flag.directive';
import _ from 'lodash';

@Component({
  selector: 'admin-add-edit-organization',
  templateUrl: './add-edit-organization.component.html',
  styleUrls: ['./add-edit-organization.component.scss'],
  imports: [
    HelpComponent,
    NgIf,
    BadgeOrganizationComponent,
    FormsModule,
    NgSelectComponent,
    NgFor,
    SwitchComponent,
    NgOptionComponent,
    NgbInputDatepicker,
    AdminOrganizationUsersComponent,
    OrganizationContractsComponent,
    AsyncPipe,
    TranslatePipe,
    LabelComponent,
    FeatureFlagDirective,
  ],
})
export class AddEditOrganizationComponent implements OnInit {
  mode: EditMode;

  organization: CreateOrUpdateOrganizationRequest;
  createContract = true;
  contract: EditableContract = { name: null, cdnEnabled: false, type: 'POC', holder: null };
  countries: Country[] = [];
  clusters: Cluster[] = [];
  availableClusters: Cluster[] = [];
  users$: Observable<Array<User>>;
  userInput$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  loading: boolean;

  clustersRead: ClusterDelegation[] = [];
  clustersAdmin: ClusterDelegation[] = [];
  disableValidate = false;

  constructor(
    private http: HttpClient,
    private adminClusterService: AdminClusterService,
    private auth: AuthService,
    private toastr: ToastrService,
    private adminOrganizationService: AdminOrganizationService,
    private adminContractService: AdminContractService,
    private countryService: CountryService,
    private route: ActivatedRoute,
    private location: Location,
  ) {}

  async ngOnInit() {
    this.loadUsersOrganization();

    await this.countryService.init();
    this.countries = this.countryService.getCountries();

    const routeParams = this.route.snapshot.paramMap;
    const organizationCode = routeParams.get('organizationCode');
    if (organizationCode) {
      this.mode = 'update';
      this.loadOrganization(organizationCode);
    } else {
      this.mode = 'create';
      this.organization = this.emptyOrganization();
    }

    this.route.data.subscribe((v) => {
      combineLatest([
        this.adminClusterService.getAll(),
        this.mode == 'update' ? this.getOrganizationClustersDelegations(organizationCode) : of(<ClusterDelegation[]>[]),
      ]).subscribe(([clusters, delegations]) => {
        this.clusters = clusters;
        clusters.forEach((cluster) => (cluster['disabled'] = !cluster.opened));
        const delegationsByRole = _.groupBy(delegations, 'role');
        this.clustersRead = delegationsByRole[CLUSTER_DELEGATION_USE] ?? [];
        this.clustersAdmin = delegationsByRole[CLUSTER_DELEGATION_ADMIN] ?? [];
        this.filterAvailableCluster();
      });
    });
  }

  loadOrganization(organizationCode: string) {
    this.adminOrganizationService.getOrganization$(organizationCode).subscribe({
      next: o => {
        this.organization = _.omit(o,['updatedAt','createdAt']);
      },
      error: (err: HttpErrorResponse) => catchError(async () => this.toastr.error(err.message)),
    });
  }

  public loadUsersOrganization(): void {
    this.users$ = this.userInput$.pipe(
      distinctUntilChanged(),
      tap(() => (this.loading = true)),
      switchMap((term: string) =>
        this.adminOrganizationService.getOrganizationUsers(this.organization.code, term).pipe(
          tap(() => (this.loading = false)),
          map((page) => page.content.map((organizationUser) => organizationUser.user)),
        ),
      ),
    );
  }

  emptyOrganization(): CreateOrUpdateOrganizationRequest {
    return {
      countryCode: 'FR',
      owner: { email: '' },
      requireMfa: false,
      featureFlags: []
    };
  }

  onCreate() {
    this.disableValidate = true;
    const createOrganizationObservable = this.createOrganization(this.organization);

    const observable: Observable<any> = this.createContract
      ? createOrganizationObservable.pipe(
          mergeMap((organization) => this.doCreateContract(organization, this.contract)),
        )
      : createOrganizationObservable;

    observable.subscribe({
      next: async () => {
        // reload organizations list
        await this.auth.loadOrganizations();
        this.back();
      },
      error: () => {
        this.disableValidate = false;
        this.toastr.error();
      },
    });
  }

  doCreateContract(holder: { code: string }, contract: EditableContract) {
    return this.adminContractService
      .addContract({
        ...contract,
        name: contract.name ?? this.organization.name,
        holder: { code: holder.code },
        organizations: [holder.code],
      })
      .pipe(map((contract) => [holder, contract] as const));
  }

  createOrganization(organization: CreateOrUpdateOrganizationRequest) {
    if (!isValidEmail(organization?.owner?.email)) {
      this.toastr.error('InvalidEmail');
      return NEVER;
    }

    return this.http.post<{
      code: string;
    }>(this.getOrganizationUrl(), this.createFormatData(organization));
  }

  updateOrganization(organization: CreateOrUpdateOrganizationRequest) {
    if (organization?.owner?.email) {
      organization.owner.email = organization.owner.email.trim();
      if (!isValidEmail(organization.owner.email)) {
        return this.toastr.error('InvalidEmail');
      }
    }

    this.http.put(this.getOrganizationUrl(organization.code), this.updateFormatData(organization)).subscribe({
      next: () => this.back(),
      error: () => this.toastr.error(),
    });
  }

  filterAvailableCluster() {
    const clusterIds = this.clustersRead.concat(this.clustersAdmin).map((c) => c.uid);
    this.availableClusters = this.clusters.filter((cluster) => !clusterIds.includes(cluster.uid));
  }

  createFormatData(organization) {
    return {
      ...organization,
      clusters: this.clustersFormData(),
    };
  }

  updateFormatData(organization) {
    let { owner, ...rest } = organization;

    return {
      clusters: this.clustersFormData(),
      ownerEmail: owner.email,
      ...rest,
    };
  }

  clustersFormData() {
    return _.fromPairs(
      _.concat(
        this.clustersRead.map((c) => [c.uid, CLUSTER_DELEGATION_USE]),
        this.clustersAdmin.map((c) => [c.uid, CLUSTER_DELEGATION_ADMIN]),
      ),
    );
  }

  getOrganizationClustersDelegations(organizationCode: string): Observable<ClusterDelegation[]> {
    return this.getDelegationService(organizationCode)
      .getReceivedDelegations({ page: 0, size: 50 })
      .pipe(
        map((delegations) =>
          delegations.content.map((delegation) => ({
            uid: delegation.cluster.uid,
            name: delegation.cluster.name,
            role: delegation.role,
          })),
        ),
      );
  }

  private assignContract(organization: { code: string }, contract: Contract) {
    return this.adminContractService.organizations(contract.number).add(organization.code);
  }

  private getDelegationService(organizationCode: string): ClusterDelegationsService {
    return {
      getReceivedDelegations: (pageParams: PageParams) =>
        this.adminOrganizationService.getReceivedDelegations(organizationCode, pageParams),
    };
  }

  private getOrganizationUrl(organizationCode: string = null, suffix: string = '') {
    let url = 'admin/organizations';
    if (organizationCode !== null) {
      url += `/${organizationCode}${suffix}`;
    }
    return url;
  }

  back() {
    this.location.back();
  }

  canValidate() {
    return !this.disableValidate && this.organization.name && isValidEmail(this.organization?.owner?.email);
  }

  protected readonly ContractTypes = ContractTypes;
  protected readonly FEATURE_FLAGS = FEATURE_FLAGS;
}

type ClusterDelegation = ClusterUidAndName & {
  role: ClusterRole;
};

type User = {
  firstName?: string;
  lastName?: string;
  email: string;
};

type CreateOrUpdateOrganizationRequest = Partial<AdminOrganizationInfo>;
